import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireTimeOffId(id) {
  const trimmed = String(id || "").trim();
  if (!trimmed) {
    throw new ApiError("id is required", 400);
  }
  return trimmed;
}

function appendIfPresent(query, key, value) {
  if (value == null) return;
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    query.set(key, value.filter(Boolean).join(","));
    return;
  }
  const str = String(value).trim();
  if (!str) return;
  query.set(key, str);
}

/**
 * GET /api/admin/time-off — list time-off entries in a date range.
 *
 * Required: from, to. Optional: tenantId (used only for country-matching imported
 * holidays — when omitted, all holidays from all countries are returned).
 */
export async function fetchAdminTimeOffList({
  token,
  tenantId,
  from,
  to,
  scope,
  type,
  source,
  doctorId,
  limit,
  skip,
} = {}) {
  const fromIso = String(from || "").trim();
  const toIso = String(to || "").trim();
  if (!fromIso || !toIso) throw new ApiError("from and to are required", 400);

  const query = new URLSearchParams();
  query.set("from", fromIso);
  query.set("to", toIso);
  appendIfPresent(query, "tenantId", tenantId);
  appendIfPresent(query, "scope", scope);
  appendIfPresent(query, "type", type);
  appendIfPresent(query, "source", source);
  appendIfPresent(query, "doctorId", doctorId);
  if (Number.isFinite(limit) && limit > 0) query.set("limit", String(limit));
  if (Number.isFinite(skip) && skip > 0) query.set("skip", String(skip));

  return apiRequest(`/api/admin/time-off?${query.toString()}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * GET /api/admin/time-off/{id} — single fetch.
 */
export async function fetchAdminTimeOff({ token, id } = {}) {
  const trimmed = requireTimeOffId(id);
  return apiRequest(`/api/admin/time-off/${encodeURIComponent(trimmed)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * POST /api/admin/time-off — create a custom block (server forces source=custom).
 *
 * Body shape: { tenantId, scope:"global"|"doctor", type, ownerId?, relatedDoctorId?,
 *               allDay, title, description?, time:{ start, end, timezone } }.
 */
export async function createAdminTimeOff({ token, body } = {}) {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/time-off`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/admin/time-off/{id} — partial update.
 */
export async function updateAdminTimeOff({ token, id, body } = {}) {
  const trimmed = requireTimeOffId(id);
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    throw new ApiError("body is required", 400);
  }
  return apiRequest(`/api/admin/time-off/${encodeURIComponent(trimmed)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}

/**
 * DELETE /api/admin/time-off/{id} — soft-delete.
 */
export async function deleteAdminTimeOff({ token, id } = {}) {
  const trimmed = requireTimeOffId(id);
  return apiRequest(`/api/admin/time-off/${encodeURIComponent(trimmed)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * POST /api/admin/time-off/import — import public holidays from Nager.Date for
 * (country, year). Imported holidays are cross-tenant: one row per (country, year,
 * holiday) is shared across every tenant in that country. `tenantId` is optional and
 * used only as a fallback to default `country`/`timezone` when those are omitted.
 *
 * `dryRun:true` returns a preview without writes. `skipLocalKeys` drops individual
 * entries from a follow-up commit.
 */
export async function importHolidays({ token, tenantId, country, timezone, year, dryRun, skipLocalKeys } = {}) {
  if (!Number.isInteger(year) || year < 1900 || year > 2200) {
    throw new ApiError("year must be an integer between 1900 and 2200", 400);
  }
  const body = { year };
  const tid = String(tenantId || "").trim();
  if (tid) body.tenantId = tid;
  if (country) body.country = String(country).trim();
  if (timezone) body.timezone = String(timezone).trim();
  if (dryRun) body.dryRun = true;
  if (Array.isArray(skipLocalKeys) && skipLocalKeys.length > 0) {
    body.skipLocalKeys = skipLocalKeys.filter(Boolean);
  }
  if (!body.country && !body.tenantId) {
    throw new ApiError("country (or tenantId for fallback) is required", 400);
  }
  return apiRequest(`/api/admin/time-off/import`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(body),
  });
}
